import { useState, useContext, useCallback } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import "./amchartsLicense";
import {
  GeneralAppContext,
  THEMES_NAMES,
} from "../../General/GeneralAppProvider";
import { DarkChartTheme } from "./DarkChartTheme";
import { LightChartTheme } from "./LightChartTheme";
import { useDebounce } from "react-use";
import type { ThemeName } from "../../types/GeneralAppProvider";

export interface CreateChartDefs {
  isScrollable?: boolean;
  hideLegend?: boolean;
  adjustableLegend?: boolean;
  displayFullLegend?: boolean;
}

export interface UseChartReturn {
  chartLoading: boolean;
  dataError: boolean;
  dataExists: boolean | null;
  startLoadingChart: () => void;
  stopLoadingChart: () => void;
  setChartError: (isError: boolean) => void;
  createChart: <T extends am4core.Sprite>(
    chartName: string,
    chartType: { new (): T },
    defs?: CreateChartDefs
  ) => [T, am4core.Container | undefined];
  setChartDataExists: (isData: boolean | null) => void;
}

const useChart = (
  setIsReadyToExport: ((isReady: boolean) => void) | null = null
): UseChartReturn => {
  const [chartLoading, setChartLoading] = useState(true);
  const [dataError, setDataError] = useState(false);
  const [dataExists, setDataExists] = useState<boolean | null>(null);
  const [debouncedTheme, setDebouncedTheme] = useState<ThemeName | null>(null);

  const {
    appContext: { currentTheme },
  } = useContext(GeneralAppContext);

  useDebounce(
    () => {
      // since the change of theme causes big re-render of the dashboard,
      // this allows the rest of the page to perform scss change before it triggers the chart re-render
      setDebouncedTheme(currentTheme ?? null);
    },
    200,
    [currentTheme]
  );

  const startLoadingChart = () => {
    setChartLoading(true);
    setIsReadyToExport?.(false);
  };

  const stopLoadingChart = () => {
    setChartLoading(false);
    setIsReadyToExport?.(true);
  };

  const setChartError = (isError: boolean) => {
    setDataError(isError);
  };

  const setChartDataExists = (isData: boolean | null) => {
    setDataExists(isData);
  };

  const createChart = useCallback(
    <T extends am4core.Sprite>(
      chartName: string,
      chartType: { new (): T },
      defs?: CreateChartDefs
    ): [T, am4core.Container | undefined] => {
      const createdChart = am4core.create(chartName, chartType);
      // `create<T>` only constrains to Sprite, but the instance built here is
      // always a chart subclass (e.g. XYChart) exposing the chart-specific
      // members accessed below, so we view it as XYChart internally.
      const chart = createdChart as unknown as am4charts.XYChart;

      if (debouncedTheme === THEMES_NAMES.LIGHT || !!setIsReadyToExport) {
        am4core.unuseTheme(DarkChartTheme);
        am4core.useTheme(LightChartTheme);
      } else {
        am4core.unuseTheme(LightChartTheme);
        am4core.useTheme(DarkChartTheme);
      }

      if (chart?.logo) {
        chart.logo.disabled = true;
      }

      chart.paddingTop = 38;
      chart.preloader!.disabled = true;

      if (defs?.isScrollable) {
        chart.cursor = new am4charts.XYCursor();

        chart.scrollbarX = new am4core.Scrollbar();
        chart.scrollbarY = new am4core.Scrollbar();

        chart.cursor.behavior = "zoomXY";
      }

      let legendContainer: am4core.Container | undefined;
      if (!defs?.hideLegend) {
        legendContainer = am4core.create(
          `${chartName}-legend-container`,
          am4core.Container
        );
        legendContainer.width = am4core.percent(100);
        legendContainer.height = am4core.percent(100);
        if (legendContainer?.logo) {
          legendContainer.logo.disabled = true;
        }
        chart.legend = new am4charts.Legend();
        chart.legend.parent = legendContainer;
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.contentAlign = "center";
        chart.legend.valign = "middle";
        chart.paddingBottom = 0;

        if (defs?.adjustableLegend) {
          const resizeLegend = () => {
            const containerHeight = chart.legend.contentHeight;
            const legendContainer = document.getElementById(
              `${chartName}-legend-container`
            )!;
            let maxHeight = containerHeight;
            if (containerHeight > 80 && !defs?.displayFullLegend) {
              maxHeight = 80;
              chart.legend.scrollable = true;
            }
            legendContainer.style.maxHeight = maxHeight + "px";

            if (defs.displayFullLegend) {
              legendContainer.style.maxHeight = "unset";
              legendContainer.style.height = containerHeight + "px";
            }
          };

          chart.events.on("datavalidated", resizeLegend);
          chart.events.on("maxsizechanged", resizeLegend);

          chart.legend.events.on("datavalidated", resizeLegend);
          chart.legend.events.on("maxsizechanged", resizeLegend);
        }
      }

      return [createdChart, legendContainer];
    },
    [debouncedTheme]
  );

  return {
    chartLoading,
    dataError,
    dataExists,
    startLoadingChart,
    stopLoadingChart,
    setChartError,
    createChart,
    setChartDataExists,
  };
};

export default useChart;
